import React from 'react';
import { RouteComponentProps } from 'react-router-dom';

import {
  AgencyLegalError,
  AgencyLegalLoading,
  AgencyLegalShell,
  ExternalLink,
  LegalDocumentMeta,
  LegalRouteParams,
  PRIVACY_POLICY_LAST_REVISED,
  SectionWithId,
  SupportEmailLink,
  agencyLegalStyles as styles,
  useAgencyLegalDisplayName,
} from './agencyLegalShared';

export default function AgencyPrivacyPolicyPage({ match }: RouteComponentProps<LegalRouteParams>) {
  const { displayName, isLoading, error } = useAgencyLegalDisplayName(match.params.crmToken);

  if (isLoading) {
    return <AgencyLegalLoading />;
  }

  if (error || !displayName) {
    return <AgencyLegalError />;
  }

  return (
    <AgencyLegalShell>
      <h1 style={styles.title}>Privacy Policy {displayName}</h1>
      <LegalDocumentMeta lastRevised={PRIVACY_POLICY_LAST_REVISED} />

      <hr style={styles.divider} />

      <section id="introduction" style={styles.section}>
        <h2 style={styles.sectionTitle}>INTRODUCTION</h2>
        <p style={styles.paragraph}>
          Thank you for using {displayName}, a mobile application powered by Comcate Software,
          Inc. (&quot;Comcate,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). We are
          committed to protecting your personal information and your right to privacy. If you have
          questions or concerns about this policy or our practices, please contact us at{' '}
          <SupportEmailLink />.
        </p>
        <p style={styles.paragraph}>
          This privacy policy applies to all information collected through our mobile application
          and any related services (collectively, the &quot;App&quot;). Please read it carefully
          before using the App.
        </p>
      </section>

      <section aria-label="Table of contents" style={styles.toc}>
        <h2 style={styles.sectionTitle}>TABLE OF CONTENTS</h2>
        <ol style={styles.tocList}>
          <li>What Information Do We Collect?</li>
          <li>How Do We Use Your Information?</li>
          <li>Will Your Information Be Shared With Anyone?</li>
          <li>Third-Party Service Providers</li>
          <li>Do We Use Google Maps?</li>
          <li>Notifications</li>
          <li>How Long Do We Keep Your Information?</li>
          <li>How Do We Keep Your Information Safe?</li>
          <li>Do We Collect Information From Minors?</li>
          <li>What Are Your Privacy Rights?</li>
          <li>California Residents — Specific Privacy Rights</li>
          <li>Do We Make Updates To This Policy?</li>
          <li>How Can You Contact Us?</li>
          <li>How Can You Review, Update, or Delete Your Data?</li>
        </ol>
      </section>

      <SectionWithId id="collect" title="1. WHAT INFORMATION DO WE COLLECT?">
        <h3 style={styles.subheading}>Personal information you provide to us</h3>
        <p style={styles.paragraph}>
          We collect personal information that you voluntarily provide when registering for an account
          or using the App, including:
        </p>
        <p style={styles.paragraph}>
          Name and contact data: First and last name, email address, phone number, and postal address.
        </p>
        <p style={styles.paragraph}>
          Account credentials: Username, password, and similar authentication data. Account
          authentication is managed by Auth0, a third-party identity provider. We do not store your
          password directly.
        </p>
        <p style={styles.paragraph}>
          Service request data: Information you submit when creating or tracking a service request,
          including descriptions, photos, and location data associated with the request.
        </p>
        <p style={styles.paragraph}>
          We do not collect payment information. No purchases are made through this App.
        </p>

        <h3 style={styles.subheading}>Information collected automatically</h3>
        <p style={styles.paragraph}>When you use the App, we may automatically collect:</p>
        <p style={styles.paragraph}>
          Location data: With your permission, we collect geolocation data from your device to help
          you submit accurate service requests. You may disable location access at any time in your
          device settings.
        </p>
        <p style={styles.paragraph}>
          Device information: Device type, operating system, unique device identifiers, and mobile
          network information.
        </p>
        <p style={styles.paragraph}>
          Usage data: App features accessed, time and date of use, and interaction patterns. We may
          use this data, including in aggregate or de-identified form, to improve the App&apos;s
          performance and usability.
        </p>
        <p style={styles.paragraph}>
          Camera and storage access: With your permission, the App may access your device&apos;s
          camera and photo storage to allow you to attach photos to service requests. We do not
          access your camera or storage for any other purpose.
        </p>
      </SectionWithId>

      <SectionWithId id="use" title="2. HOW DO WE USE YOUR INFORMATION?">
        <p style={styles.paragraph}>
          We use the information we collect to: Create and manage your account. Process and track
          your service requests. Send you service-related status updates about your service requests,
          including by email and, where you have provided a mobile number or opted in, by SMS. You
          may update your notification preferences at any time through your account profile, although
          we may still send notices that are necessary to provide the App or respond to your requests.
          Improve App performance, features, and user experience. Comply with legal obligations.
          Fulfill obligations to the municipality whose App instance you are using.
        </p>
        <p style={styles.paragraph}>
          We do not use your information for advertising and we do not sell it to third parties.
        </p>
      </SectionWithId>

      <SectionWithId id="share" title="3. WILL YOUR INFORMATION BE SHARED WITH ANYONE?">
        <p style={styles.paragraph}>We share your information only in the following circumstances:</p>
        <p style={styles.paragraph}>
          With your municipality: Service request data you submit is shared with the city or local
          government agency whose App instance you are using. That agency&apos;s own privacy
          practices govern their use of your data once received.
        </p>
        <p style={styles.paragraph}>
          With service providers: We share data with third-party vendors who help us operate the App
          (see Section 4).
        </p>
        <p style={styles.paragraph}>
          For legal compliance: We may disclose information where required by law, court order, or
          governmental request.
        </p>
        <p style={styles.paragraph}>
          For safety: We may disclose information where necessary to protect the safety of any person
          or to investigate potential fraud or policy violations.
        </p>
        <p style={styles.paragraph}>
          Business transfers: In the event of a merger, acquisition, financing, reorganization,
          bankruptcy, or sale of assets, your information may be transferred as part of that
          transaction. We will provide notice where required by applicable law.
        </p>
        <p style={styles.paragraph}>We do not sell your personal information.</p>
      </SectionWithId>

      <SectionWithId id="third-party" title="4. THIRD-PARTY SERVICE PROVIDERS">
        <p style={styles.paragraph}>
          We work with the following third-party service providers who may process your data on our
          behalf:
        </p>
        <p style={styles.paragraph}>
          Auth0: Manages account authentication and login. Auth0 Privacy Policy:{' '}
          <ExternalLink href="https://auth0.com/privacy">https://auth0.com/privacy</ExternalLink>
        </p>
        <p style={styles.paragraph}>
          Google Maps: Powers location and mapping features within the App. Google Privacy Policy:{' '}
          <ExternalLink href="https://policies.google.com/privacy">
            https://policies.google.com/privacy
          </ExternalLink>
        </p>
        <p style={styles.paragraph}>
          Mailgun and other notification service providers: Powers email notification delivery for
          service request status updates. Mailgun Privacy Policy:{' '}
          <ExternalLink href="https://www.mailgun.com/legal/privacy-policy/">
            https://www.mailgun.com/legal/privacy-policy/
          </ExternalLink>
        </p>
        <p style={styles.paragraph}>
          City-configured integrations: The municipality operating your App instance may have enabled
          integrations with third-party systems such as work order or permit platforms. Data shared
          through those integrations is governed by the applicable integration&apos;s privacy
          practices and the municipality&apos;s own policies.
        </p>
        <p style={styles.paragraph}>
          We require our service providers to handle personal information securely and only for the
          purposes we specify or as otherwise permitted by their agreements with us.
        </p>
      </SectionWithId>

      <SectionWithId id="google-maps" title="5. DO WE USE GOOGLE MAPS?">
        <p style={styles.paragraph}>
          Yes. This App uses the Google Maps API to provide location-based features, including helping
          you identify the location of a service request. By using these features, you agree to be
          bound by Google&apos;s Terms of Service:{' '}
          <ExternalLink href="https://policies.google.com/terms">
            https://policies.google.com/terms
          </ExternalLink>
          . We encourage you to review Google&apos;s Privacy Policy at{' '}
          <ExternalLink href="https://policies.google.com/privacy">
            https://policies.google.com/privacy
          </ExternalLink>{' '}
          to understand how Google handles location data.
        </p>
      </SectionWithId>

      <SectionWithId id="notifications" title="6. NOTIFICATIONS">
        <p style={styles.paragraph}>
          When you create an account or submit a service request, you may receive service-related email
          and SMS notifications about your requests. SMS notifications are sent only where you have
          provided a mobile number or otherwise opted in. Message and data rates may apply. You may
          update or turn off optional notification channels at any time through your account profile
          settings or, for SMS, by following the opt-out instructions in the message.
        </p>
      </SectionWithId>

      <SectionWithId id="retention" title="7. HOW LONG DO WE KEEP YOUR INFORMATION?">
        <p style={styles.paragraph}>
          When you delete your account, we will remove or disable your authentication credentials from
          our identity provider within 30 days, unless a longer period is required or permitted by law.
          Your profile information and service request records are maintained within the
          municipality&apos;s instance of our platform and are subject to that municipality&apos;s data
          retention and public records requirements. This information may not be deleted upon account
          termination.
        </p>
        <p style={styles.paragraph}>
          When we have no ongoing legitimate business need to process your personal information, we will
          either delete or anonymize it, or securely store it and isolate it from further processing
          until deletion is possible.
        </p>
      </SectionWithId>

      <SectionWithId id="security" title="8. HOW DO WE KEEP YOUR INFORMATION SAFE?">
        <p style={styles.paragraph}>
          We implement appropriate technical and organizational security measures to protect your
          personal information, including encrypted data transmission, access controls, and secure
          hosting infrastructure. However, no method of transmission over the internet or electronic
          storage is 100% secure. You use the App at your own risk and should access it only in secure
          environments.
        </p>
      </SectionWithId>

      <SectionWithId id="minors" title="9. DO WE COLLECT INFORMATION FROM MINORS?">
        <p style={styles.paragraph}>
          We do not knowingly collect personal information from children under 13 years of age. The App
          is intended for use by adults submitting service requests on behalf of themselves or their
          household. If we become aware that we have collected data from a child under 13 without
          parental consent, we will delete that information promptly. If you believe we have collected
          such data, please contact us at <SupportEmailLink />.
        </p>
      </SectionWithId>

      <SectionWithId id="rights" title="10. WHAT ARE YOUR PRIVACY RIGHTS?">
        <p style={styles.paragraph}>
          Depending on where you live, you may have the right to: Access the personal information we hold
          about you. Correct inaccurate personal information. Request deletion of your personal
          information. Object to or restrict certain processing of your data. Receive a copy of your data
          in a portable format.
        </p>
        <p style={styles.paragraph}>
          To exercise any of these rights, please contact us at <SupportEmailLink />. We will respond
          within the timeframe required by applicable law. Please note that certain rights may be limited
          where data is held by or on behalf of a municipality subject to public records laws, or where
          Comcate processes data as a service provider to that municipality.
        </p>
      </SectionWithId>

      <SectionWithId id="california" title="11. CALIFORNIA RESIDENTS — SPECIFIC PRIVACY RIGHTS">
        <p style={styles.paragraph}>
          If you are a California resident, and to the extent the California Consumer Privacy Act (CCPA),
          as amended by the California Privacy Rights Act (CPRA), applies to your information, you may
          have specific rights, including:
        </p>
        <p style={styles.paragraph}>
          Right to know: You may request disclosure of the categories and specific pieces of personal
          information we have collected about you.
        </p>
        <p style={styles.paragraph}>
          Right to delete: You may request deletion of your personal information, subject to certain
          exceptions including data held subject to municipal public records retention requirements.
        </p>
        <p style={styles.paragraph}>
          Right to correct: You may request correction of inaccurate personal information.
        </p>
        <p style={styles.paragraph}>
          Right to opt out of sale or sharing: We do not sell or share your personal information for
          cross-context behavioral advertising.
        </p>
        <p style={styles.paragraph}>
          Right to limit use of sensitive personal information: We use precise location information and
          other sensitive personal information only as necessary to provide the App&apos;s core
          functionality and as otherwise permitted by applicable law.
        </p>
        <p style={styles.paragraph}>
          Non-discrimination: We will not discriminate against you for exercising any of these rights.
        </p>
        <p style={styles.paragraph}>
          To submit a request, contact us at <SupportEmailLink />. We will respond within the timeframe
          required by applicable law.
        </p>
        <p style={styles.paragraph}>
          Additionally, under California Civil Code Section 1798.83 (&quot;Shine the Light&quot;),
          California residents may request information about personal information disclosed to third
          parties for direct marketing purposes. We do not disclose personal information to third parties
          for direct marketing.
        </p>
      </SectionWithId>

      <SectionWithId id="updates" title="12. DO WE MAKE UPDATES TO THIS POLICY?">
        <p style={styles.paragraph}>
          We may update this policy from time to time to reflect changes in our practices or applicable
          law. When we do, we will update the &quot;Last Revised&quot; date at the top of this policy.
          For material changes, we will provide notice through the App or by email. We encourage you to
          review this policy periodically.
        </p>
      </SectionWithId>

      <SectionWithId id="contact" title="13. HOW CAN YOU CONTACT US?">
        <p style={styles.paragraph}>
          Comcate Software, Inc. 9450 SW Gemini Dr PMB 61173
          <br />
          Beaverton, Oregon 97008-7105 US
          <br />
          Email: <SupportEmailLink /> Phone: <a href="tel:+14156321249">(415) 632-1249</a>
        </p>
      </SectionWithId>

      <SectionWithId id="review-delete" title="14. HOW CAN YOU REVIEW, UPDATE, OR DELETE YOUR DATA?">
        <p style={styles.paragraph}>
          You may request deletion or disabling of your account authentication credentials by contacting
          us at <SupportEmailLink />. We will process this within 30 days, unless a longer period is
          required or permitted by law. Please note that your profile information and any service request
          records submitted through the App are maintained by your local government agency and may not be
          deleted by Comcate upon request. These records may be subject to the municipality&apos;s public
          records retention requirements.
        </p>
        <p style={styles.paragraph}>
          To review or correct your personal information, you may log into your account profile directly
          or contact us at <SupportEmailLink />.
        </p>
      </SectionWithId>
    </AgencyLegalShell>
  );
}
